export const listingFiltersMethods = {
    getFilterUiRoots() {
        return [
            this.filtersSidebar?.content,
            this.desktopFiltersRoot ||
                this.root?.querySelector('[data-listing-desktop-filters]'),
        ].filter(Boolean);
    },

    syncPendingFiltersFromState() {
        this.pendingFilters = this.cloneFilters(this.state.filters || {});
        this.syncPendingPriceFromState();
        this.expandPendingHierarchicalFilters();
        this.expandPendingFilterAncestors();
        this.updatePendingFilterSelectionUi();
        this.updateSortUi(this.state.sort, { syncState: false, scope: 'sidebar' });
        this.syncListingFilterAccordions();
    },

    syncPendingPriceFromState() {
        this.pendingPriceMin =
            this.state.priceMin !== null && this.state.priceMin !== undefined
                ? String(this.state.priceMin)
                : '';
        this.pendingPriceMax =
            this.state.priceMax !== null && this.state.priceMax !== undefined
                ? String(this.state.priceMax)
                : '';
        this.updatePendingPriceInputs();
    },

    getPriceInputElements() {
        const roots = this.getFilterUiRoots();

        return {
            min: roots
                .map(root => root.querySelector('[data-listing-price-min]'))
                .find(Boolean),
            max: roots
                .map(root => root.querySelector('[data-listing-price-max]'))
                .find(Boolean),
        };
    },

    updatePendingPriceInputs() {
        const { min, max } = this.getPriceInputElements();

        if (min) {
            min.value = this.pendingPriceMin;
        }

        if (max) {
            max.value = this.pendingPriceMax;
        }
    },

    getPendingPriceRange() {
        return this.normalizePriceRange(
            this.pendingPriceMin,
            this.pendingPriceMax
        );
    },

    getSidebarFilterTaxonomies() {
        const taxonomies = new Set(this.getFilterTaxonomies());

        this.getFilterUiRoots().forEach(root => {
            root.querySelectorAll(
                '[data-listing-filter-select][data-taxonomy]'
            ).forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';

                if (taxonomy) {
                    taxonomies.add(taxonomy);
                }
            });
        });

        return Array.from(taxonomies);
    },

    resetPendingSidebarFilters() {
        this.pendingFilters = {};

        this.getSidebarFilterTaxonomies().forEach(taxonomy => {
            this.pendingFilters[taxonomy] = [];
        });

        this.pendingPriceMin = '';
        this.pendingPriceMax = '';
        this.updatePendingPriceInputs();
    },

    getEmptyListingFilters() {
        return this.getSidebarFilterTaxonomies().reduce((acc, taxonomy) => {
            acc[taxonomy] = [];
            return acc;
        }, {});
    },

    clearSidebarFilters({ applyToListing = false } = {}) {
        this.resetPendingSidebarFilters();
        this.updatePendingFilterSelectionUi();
        this.updateSortUi(this.state.sort, { syncState: false, scope: 'sidebar' });

        if (!applyToListing) {
            return;
        }

        this.state.filters = this.getEmptyListingFilters();
        this.state.priceMin = null;
        this.state.priceMax = null;
        this.state.page = 1;
        this.filtersSidebar?.close();
        this.fetchAndRender({ pushUrl: this.getListingUrl() });
    },

    clearAppliedListingFilters() {
        this.state.filters = this.getEmptyListingFilters();
        this.state.priceMin = null;
        this.state.priceMax = null;
        this.state.page = 1;
        this.clearSidebarFilters({ applyToListing: false });
        this.filtersSidebar?.close();
        this.fetchAndRender({ pushUrl: this.getListingUrl() });
    },

    usesHierarchicalFilterSelection(taxonomy) {
        return taxonomy === 'product_cat';
    },

    isSlugCoveredByPendingSelection(taxonomy, slug) {
        if (!taxonomy || !slug) {
            return false;
        }

        const pending = this.pendingFilters[taxonomy] || [];

        if (pending.includes(slug)) {
            return true;
        }

        if (!this.usesHierarchicalFilterSelection(taxonomy)) {
            return false;
        }

        const descendantsMap = this.bootstrap.termDescendants?.[taxonomy] || {};

        return pending.some(selectedSlug => {
            const branch = descendantsMap[selectedSlug];

            return Array.isArray(branch) && branch.includes(slug);
        });
    },

    getActiveSortFromMarkup(root = null) {
        const scopes = root
            ? [root]
            : [this.sortRoot, this.filtersSidebar?.content].filter(Boolean);

        for (const scope of scopes) {
            const active = scope.querySelector(
                '[data-listing-sort-option][aria-pressed="true"]'
            );

            if (active?.dataset?.sortValue) {
                return active.dataset.sortValue;
            }
        }

        return this.state?.sort || 'popular';
    },

    applyListingSort(
        nextSort,
        { fetch = true, pushUrl = true, closeSidebar = false } = {}
    ) {
        const normalized = String(nextSort || 'popular').trim() || 'popular';

        this.state.sort = normalized;
        this.state.page = 1;
        this.updateSortUi(normalized);

        if (closeSidebar) {
            this.filtersSidebar?.close();
        }

        if (!fetch) {
            return;
        }

        this.fetchAndRender({
            pushUrl: pushUrl ? this.getListingUrl() : null,
        });
    },

    applyPendingSidebarToState() {
        const sidebarContent = this.filtersSidebar?.content;

        if (sidebarContent) {
            this.state.sort = this.getActiveSortFromMarkup(sidebarContent);
        }

        this.state.filters = this.cloneFilters(this.pendingFilters);

        const price = this.getPendingPriceRange();
        this.state.priceMin = price.min;
        this.state.priceMax = price.max;
        this.state.page = 1;
        this.updateSortUi(this.state.sort);
    },

    schedulePendingFiltersCountPreview() {
        if (this.pendingFiltersCountTimer) {
            window.clearTimeout(this.pendingFiltersCountTimer);
        }

        this.pendingFiltersCountTimer = window.setTimeout(() => {
            this.pendingFiltersCountTimer = null;
            this.applyPendingSidebarToState();
            this.fetchAndRender({ pushUrl: this.getListingUrl() });
        }, 200);
    },

    getFiltersApplyLabelElement() {
        return (
            this.filtersSidebar?.content?.querySelector(
                '[data-listing-filters-apply-label]'
            ) || null
        );
    },

    formatFiltersApplyLabel(total) {
        const normalized = Math.max(0, Number(total) || 0);

        return `Wyświetl ${normalized.toLocaleString('pl-PL')}`;
    },

    hasFilterCountsInSidebar() {
        const roots = this.getFilterUiRoots();

        if (this.isInspirationsMode?.()) {
            const taxonomies = this.getFilterTaxonomies?.() || [];

            if (taxonomies.length > 0) {
                return taxonomies.every(taxonomy => {
                    const group = roots
                        .map(root =>
                            root.querySelector(
                                `[data-listing-filter-group-taxonomy="${taxonomy}"]`
                            )
                        )
                        .find(Boolean);

                    if (!group) {
                        return true;
                    }

                    return Array.from(
                        group.querySelectorAll('[data-listing-filter-label]')
                    ).some(label =>
                        /\(\d[\d\s.]*\)/.test(label.textContent || '')
                    );
                });
            }
        }

        return roots.some(root =>
            Array.from(
                root.querySelectorAll('[data-listing-filter-label]')
            ).some(label => /\(\d[\d\s.]*\)/.test(label.textContent || ''))
        );
    },

    buildFilterCountsRequestBody() {
        const listingState = {
            ...this.state,
            page: 1,
        };
        const body = this.buildElasticRequestBody(listingState);
        body.listing = 'filterCounts';
        const terms = [];

        this.getFilterUiRoots().forEach(root => {
            root.querySelectorAll(
                '[data-listing-filter-select][data-term-slug]'
            ).forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';
                const slug = button.dataset.termSlug || '';

                if (!taxonomy || !slug) {
                    return;
                }

                if (
                    Array.isArray(this.bootstrap.indexedFilterTaxonomies) &&
                    this.bootstrap.indexedFilterTaxonomies.length > 0 &&
                    !this.bootstrap.indexedFilterTaxonomies.includes(taxonomy)
                ) {
                    return;
                }

                const termId =
                    typeof this.resolveElasticFilterTermId === 'function'
                        ? this.resolveElasticFilterTermId(taxonomy, slug)
                        : Number(
                              this.bootstrap.termIndex?.[taxonomy]?.[slug]?.id ||
                                  0
                          );
                const entry = { taxonomy, slug };

                if (termId > 0) {
                    entry.term_id = termId;
                }

                terms.push(entry);
            });
        });

        return {
            ...body,
            terms,
        };
    },

    async fetchListingFilterCounts(requestBody) {
        const elasticHeaders = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };

        if (this.bootstrap.elasticApiToken) {
            elasticHeaders['X-Listing-Token'] = this.bootstrap.elasticApiToken;
        }

        const elasticResponse = await fetch(this.bootstrap.elasticApiUrl, {
            method: 'POST',
            headers: elasticHeaders,
            body: JSON.stringify(requestBody),
        });

        if (!elasticResponse.ok) {
            throw new Error('Elastic filter counts request failed.');
        }

        return elasticResponse.json();
    },

    async loadFilterTermCounts({ force = false } = {}) {
        if (
            !this.canUseElastic() ||
            (!force && (this.filterCountsLoaded || this.filterCountsLoading))
        ) {
            return;
        }

        const requestBody = this.buildFilterCountsRequestBody();
        const terms = requestBody.terms || [];

        if (terms.length === 0) {
            return;
        }

        this.filterCountsLoading = true;

        try {
            const elasticData =
                typeof this.postInspirationsElastic === 'function'
                    ? await this.postInspirationsElastic(requestBody)
                    : await this.fetchListingFilterCounts(requestBody);

            if (!elasticData?.success || !elasticData.counts) {
                throw new Error(
                    'Elastic filter counts response was not successful.'
                );
            }

            this.applyFilterCounts(elasticData.counts);
            this.filterCountsLoaded = true;
        } catch (error) {
            console.warn('Listing filter counts failed.', error);
        } finally {
            this.filterCountsLoading = false;
        }
    },

    shouldHideZeroFilterCounts(taxonomy = '') {
        const normalizedTaxonomy = String(taxonomy || '').trim();
        const map = this.bootstrap.filterHideZeroCounts;

        if (
            normalizedTaxonomy &&
            map &&
            typeof map === 'object' &&
            Object.prototype.hasOwnProperty.call(map, normalizedTaxonomy)
        ) {
            return Boolean(map[normalizedTaxonomy]);
        }

        return true;
    },

    getFilterCollapseVisibleLimit(taxonomy = '') {
        const normalizedTaxonomy = String(taxonomy || '').trim();
        const limits = this.bootstrap.filterVisibleLimits;

        if (
            normalizedTaxonomy &&
            limits &&
            typeof limits === 'object' &&
            Object.prototype.hasOwnProperty.call(limits, normalizedTaxonomy)
        ) {
            return Math.max(0, Number(limits[normalizedTaxonomy]) || 0);
        }

        return 10;
    },

    getCollapsibleFilterTaxonomies() {
        const fromBootstrap = this.bootstrap.collapsibleFilterTaxonomies;

        if (Array.isArray(fromBootstrap) && fromBootstrap.length > 0) {
            return fromBootstrap;
        }

        return ['room', 'style', 'collection'];
    },

    initFilterCollapseContainers() {
        const collapsibleTaxonomies = new Set(
            this.getCollapsibleFilterTaxonomies()
        );

        this.getFilterUiRoots().forEach(root => {
            root.querySelectorAll(
                '[data-listing-filter-group][data-listing-filter-group-taxonomy]'
            ).forEach(group => {
                const taxonomy = group.dataset.listingFilterGroupTaxonomy || '';

                if (!collapsibleTaxonomies.has(taxonomy)) {
                    return;
                }

                const container = group.querySelector(
                    '.listing__filter-flat, .listing__filter-tree'
                );

                if (!container) {
                    return;
                }

                if (
                    !container.hasAttribute('data-listing-filter-collapsible')
                ) {
                    container.setAttribute(
                        'data-listing-filter-collapsible',
                        ''
                    );
                    container.dataset.listingFilterCollapsibleMode =
                        container.classList.contains('listing__filter-flat')
                            ? 'flat'
                            : 'tree';
                    container.dataset.listingFilterCollapsibleLimit = String(
                        this.getFilterCollapseVisibleLimit(taxonomy)
                    );
                }
            });

            root.querySelectorAll(
                '[data-listing-desktop-filter][data-taxonomy]'
            ).forEach(filterRoot => {
                const taxonomy = filterRoot.dataset.taxonomy || '';

                if (!collapsibleTaxonomies.has(taxonomy)) {
                    return;
                }

                const container = filterRoot.querySelector(
                    '.listing__filter-flat, .listing__filter-tree'
                );

                if (!container) {
                    return;
                }

                if (
                    !container.hasAttribute('data-listing-filter-collapsible')
                ) {
                    container.setAttribute(
                        'data-listing-filter-collapsible',
                        ''
                    );
                    container.dataset.listingFilterCollapsibleMode =
                        container.classList.contains('listing__filter-flat')
                            ? 'flat'
                            : 'tree';
                    container.dataset.listingFilterCollapsibleLimit = String(
                        this.getFilterCollapseVisibleLimit(taxonomy)
                    );
                }
            });
        });
    },

    ensureFilterMoreButton(container) {
        if (!container || this.isFilterCollapseExpanded(container)) {
            return (
                container?.querySelector('[data-listing-filter-more]') || null
            );
        }

        let moreButton = container.querySelector('[data-listing-filter-more]');

        if (moreButton) {
            return moreButton;
        }

        moreButton = document.createElement('button');
        moreButton.type = 'button';
        moreButton.className = 'listing__filter-more';
        moreButton.setAttribute('data-listing-filter-more', '');
        moreButton.setAttribute('data-listing-filter-more-label', '');
        moreButton.setAttribute('aria-expanded', 'false');
        container.appendChild(moreButton);

        return moreButton;
    },

    setFilterMoreButtonVisibility(moreButton, visible, hiddenCount = 0) {
        if (!moreButton) {
            return;
        }

        const moreLabel =
            moreButton.matches('[data-listing-filter-more-label]')
                ? moreButton
                : moreButton.querySelector('[data-listing-filter-more-label]') ||
                  moreButton;

        moreButton.classList.remove('hidden', 'hidden!');
        moreButton.classList.toggle('listing__filter-more--hidden', !visible);

        if (visible) {
            moreButton.removeAttribute('hidden');
            moreButton.setAttribute('aria-expanded', 'false');
            moreLabel.textContent = `+ ${Math.max(0, Number(hiddenCount) || 0).toLocaleString(
                'pl-PL'
            )} więcej`;
            return;
        }

        moreButton.setAttribute('hidden', '');
        moreButton.setAttribute('aria-expanded', 'true');
    },

    getFilterCollapseContainerKey(container) {
        if (!container) {
            return '';
        }

        const group = container.closest('[data-listing-filter-group]');
        const taxonomy =
            group?.dataset.listingFilterGroupTaxonomy ||
            container.closest('[data-listing-desktop-filter]')?.dataset
                .taxonomy ||
            '';
        const mode = container.dataset.listingFilterCollapsibleMode || 'flat';
        const ancestorSlugs = [];
        let node = container.closest('[data-listing-filter-node]');

        while (node) {
            const button = node.querySelector(
                ':scope > .listing__filter-row [data-listing-filter-select]'
            );
            const slug = button?.dataset.termSlug || '';

            if (slug) {
                ancestorSlugs.unshift(slug);
            }

            const parentTree = node.parentElement?.closest(
                '.listing__filter-tree'
            );
            node = parentTree?.closest('[data-listing-filter-node]') || null;
        }

        return `${taxonomy}:${mode}:${ancestorSlugs.join('/')}`;
    },

    rememberFilterCollapseExpanded(container) {
        if (!container) {
            return;
        }

        const key = this.getFilterCollapseContainerKey(container);

        if (!key) {
            return;
        }

        if (!this.expandedFilterCollapseKeys) {
            this.expandedFilterCollapseKeys = new Set();
        }

        this.expandedFilterCollapseKeys.add(key);
        container.dataset.listingFilterCollapsibleExpanded = 'true';
    },

    reapplyExpandedFilterCollapseGroups() {
        if (!this.expandedFilterCollapseKeys?.size) {
            return;
        }

        this.getFilterUiRoots().forEach(root => {
            root.querySelectorAll('[data-listing-filter-collapsible]').forEach(
                container => {
                    const key = this.getFilterCollapseContainerKey(container);

                    if (!key || !this.expandedFilterCollapseKeys.has(key)) {
                        return;
                    }

                    container.dataset.listingFilterCollapsibleExpanded = 'true';
                }
            );
        });
    },

    refreshFilterCollapseUi() {
        this.initFilterCollapseContainers();
        this.reapplyExpandedFilterCollapseGroups();
        this.syncAllFilterCollapseStates();
    },

    getVisibleFilterSelectSelector() {
        return '[data-listing-filter-select]:not(.hidden):not([hidden]):not(.listing__filter-option--collapsed):not(.listing__filter-option--unavailable)';
    },

    isFilterCollapseExpanded(container) {
        if (!container) {
            return false;
        }

        if (container.dataset.listingFilterCollapsibleExpanded === 'true') {
            return true;
        }

        const key = this.getFilterCollapseContainerKey(container);

        return Boolean(key && this.expandedFilterCollapseKeys?.has(key));
    },

    getFilterCollapseEntries(container) {
        return this.collectFilterCollapseEntries(container, {
            includeCollapsed: false,
        });
    },

    getAllFilterCollapseEntries(container) {
        return this.collectFilterCollapseEntries(container, {
            includeCollapsed: true,
        });
    },

    isFilterCountHidden(button) {
        if (!button) {
            return false;
        }

        if (button.classList.contains('listing__filter-option--unavailable')) {
            return true;
        }

        const isHidden =
            button.classList.contains('hidden') ||
            button.hasAttribute('hidden');

        if (!isHidden) {
            return false;
        }

        // Collapse też ustawia hidden — nie mylić z ukryciem po facet counts.
        return !button.classList.contains('listing__filter-option--collapsed');
    },

    collectFilterCollapseEntries(container, { includeCollapsed = false } = {}) {
        if (!container) {
            return [];
        }

        const mode = container.dataset.listingFilterCollapsibleMode || 'flat';

        if (mode === 'tree') {
            return Array.from(
                container.querySelectorAll(
                    ':scope > [data-listing-filter-node]'
                )
            )
                .map(node => {
                    const button = node.querySelector(
                        ':scope > .listing__filter-row [data-listing-filter-select]'
                    );

                    return button ? { button, node } : null;
                })
                .filter(entry => {
                    if (!entry?.button) {
                        return false;
                    }

                    if (this.isFilterCountHidden(entry.button)) {
                        return false;
                    }

                    if (!includeCollapsed) {
                        return (
                            !entry.button.classList.contains('hidden') &&
                            !entry.button.hasAttribute('hidden')
                        );
                    }

                    return true;
                });
        }

        return Array.from(
            container.querySelectorAll(':scope > [data-listing-filter-select]')
        )
            .filter(button => {
                if (this.isFilterCountHidden(button)) {
                    return false;
                }

                if (!includeCollapsed) {
                    return (
                        !button.classList.contains('hidden') &&
                        !button.hasAttribute('hidden')
                    );
                }

                return true;
            })
            .map(button => ({
                button,
                node: button.closest('[data-listing-filter-node]'),
            }));
    },

    setFilterCollapseEntryState(entry, collapsed) {
        if (!entry?.button) {
            return;
        }

        entry.button.classList.toggle(
            'listing__filter-option--collapsed',
            collapsed
        );

        if (entry.node) {
            entry.node.classList.toggle(
                'listing__filter-node--collapsed',
                collapsed
            );
        }
    },

    expandFilterCollapseGroup(container) {
        if (!container) {
            return;
        }

        this.rememberFilterCollapseExpanded(container);

        this.getAllFilterCollapseEntries(container).forEach(entry => {
            this.setFilterCollapseEntryState(entry, false);
        });

        this.setFilterMoreButtonVisibility(
            container.querySelector('[data-listing-filter-more]'),
            false
        );
    },

    syncFilterCollapseState(container) {
        if (!container) {
            return;
        }

        if (this.isFilterCollapseExpanded(container)) {
            this.getAllFilterCollapseEntries(container).forEach(entry => {
                this.setFilterCollapseEntryState(entry, false);
            });

            this.setFilterMoreButtonVisibility(
                container.querySelector('[data-listing-filter-more]'),
                false
            );

            return;
        }

        const taxonomy =
            container.closest('[data-listing-filter-group]')?.dataset
                .listingFilterGroupTaxonomy ||
            container.closest('[data-listing-desktop-filter]')?.dataset
                .taxonomy ||
            '';
        const limit =
            Number(container.dataset.listingFilterCollapsibleLimit) ||
            this.getFilterCollapseVisibleLimit(taxonomy);
        const allEntries = this.getAllFilterCollapseEntries(container).filter(
            entry => entry?.button && !this.isFilterCountHidden(entry.button)
        );
        const moreButton = this.ensureFilterMoreButton(container);

        if (allEntries.length <= limit) {
            allEntries.forEach(entry => {
                this.setFilterCollapseEntryState(entry, false);
            });
            this.setFilterMoreButtonVisibility(moreButton, false);
            return;
        }

        let hiddenCount = 0;
        let visibleSlots = 0;

        allEntries.forEach(entry => {
            const ariaPressed = entry.button.getAttribute('aria-pressed');
            const isSelected =
                ariaPressed === 'true' || ariaPressed === 'mixed';
            const shouldShow = isSelected || visibleSlots < limit;

            this.setFilterCollapseEntryState(entry, !shouldShow);

            if (shouldShow) {
                visibleSlots += 1;
                return;
            }

            hiddenCount += 1;
        });

        this.setFilterMoreButtonVisibility(
            moreButton,
            hiddenCount > 0,
            hiddenCount
        );
    },

    syncAllFilterCollapseStates() {
        this.getFilterUiRoots().forEach(root => {
            root.querySelectorAll('[data-listing-filter-collapsible]').forEach(
                container => {
                    this.syncFilterCollapseState(container);
                }
            );
        });
    },

    setListingFilterOptionVisibility(button, visible) {
        if (!button) {
            return;
        }

        button.classList.toggle('listing__filter-option--unavailable', !visible);
        // Utility `hidden` przegrywa z `flex` na przycisku — nie używamy jej tu.
        button.classList.remove('hidden');

        if (visible) {
            button.removeAttribute('hidden');
            button.removeAttribute('aria-hidden');
        } else {
            button.classList.remove('listing__filter-option--collapsed');
            button.setAttribute('hidden', '');
            button.setAttribute('aria-hidden', 'true');
        }

        const node = button.closest('[data-listing-filter-node]');

        if (!node) {
            return;
        }

        node.classList.toggle('listing__filter-node--unavailable', !visible);

        if (!visible) {
            node.classList.remove('listing__filter-node--collapsed');
        }
    },

    applyFilterCounts(counts = {}) {
        this.getFilterUiRoots().forEach(sidebarRoot => {
            if (!sidebarRoot) {
                return;
            }

            sidebarRoot
                .querySelectorAll(
                    '[data-listing-filter-select][data-term-slug]'
                )
                .forEach(button => {
                    const taxonomy = button.dataset.taxonomy || '';
                    const slug = button.dataset.termSlug || '';

                    if (
                        Array.isArray(this.bootstrap.indexedFilterTaxonomies) &&
                        this.bootstrap.indexedFilterTaxonomies.length > 0 &&
                        !this.bootstrap.indexedFilterTaxonomies.includes(
                            taxonomy
                        )
                    ) {
                        return;
                    }

                    const key = `${taxonomy}:${slug}`;
                    const count = Object.prototype.hasOwnProperty.call(
                        counts,
                        key
                    )
                        ? Math.max(0, Number(counts[key]) || 0)
                        : 0;
                    const isSelected = this.isFilterSelectedInUi(
                        taxonomy,
                        slug
                    );
                    const hideZero = this.shouldHideZeroFilterCounts(taxonomy);
                    const visible =
                        !hideZero || count > 0 || isSelected;

                    this.setListingFilterOptionVisibility(button, visible);

                    if (!visible) {
                        return;
                    }

                    const labelNode = button.querySelector(
                        '[data-listing-filter-label]'
                    );

                    if (!labelNode) {
                        return;
                    }

                    const termName =
                        this.bootstrap.termIndex?.[taxonomy]?.[slug]?.name ||
                        '';
                    const labelText =
                        (labelNode.textContent || '')
                            .replace(/\s*\([\d\s.]+\)\s*$/, '')
                            .trim() || termName;

                    if (
                        hideZero ||
                        Object.prototype.hasOwnProperty.call(counts, key)
                    ) {
                        labelNode.textContent = `${labelText} (${count.toLocaleString(
                            'pl-PL'
                        )})`;
                    }
                });

            if (Object.keys(counts).length > 0) {
                this.pruneEmptyListingFilterGroups(sidebarRoot);
                this.pruneEmptyFlatFilterGroups(sidebarRoot);
                this.pruneEmptyDesktopFilters(sidebarRoot);
            }
        });

        this.refreshFilterCollapseUi();
    },

    pruneEmptyListingFilterGroups(sidebarRoot) {
        const visibleSelector = this.getVisibleFilterSelectSelector();

        sidebarRoot
            .querySelectorAll('[data-listing-filter-group]')
            .forEach(group => {
                const tree = group.querySelector('.listing__filter-tree');

                if (!tree) {
                    return;
                }

                const hasVisibleOptions = Boolean(
                    tree.querySelector(visibleSelector)
                );

                group.classList.toggle('hidden', !hasVisibleOptions);
            });

        sidebarRoot
            .querySelectorAll('[data-listing-filter-children]')
            .forEach(container => {
                const tree = container.querySelector(
                    ':scope > .listing__filter-tree'
                );

                if (!tree) {
                    return;
                }

                const hasVisibleOptions = Boolean(
                    tree.querySelector(visibleSelector)
                );

                if (!hasVisibleOptions) {
                    const node = container.closest(
                        '[data-listing-filter-node]'
                    );

                    if (!node) {
                        return;
                    }

                    this.closeListingFilterAccordion(node, {
                        animate: false,
                    });
                }
            });
    },

    pruneEmptyFlatFilterGroups(sidebarRoot) {
        const visibleSelector = this.getVisibleFilterSelectSelector();

        sidebarRoot
            .querySelectorAll('[data-listing-filter-group]')
            .forEach(group => {
                if (group.querySelector('[data-listing-sort-option]')) {
                    return;
                }

                if (group.querySelector('.listing__filter-tree')) {
                    return;
                }

                const hasVisibleOptions = Boolean(
                    group.querySelector(visibleSelector)
                );

                group.classList.toggle('hidden', !hasVisibleOptions);
            });
    },

    pruneEmptyDesktopFilters(sidebarRoot) {
        const visibleSelector = this.getVisibleFilterSelectSelector();

        sidebarRoot
            .querySelectorAll('[data-listing-desktop-filter]')
            .forEach(filterRoot => {
                const hasVisibleOptions = Boolean(
                    filterRoot.querySelector(visibleSelector)
                );

                filterRoot.classList.toggle('hidden', !hasVisibleOptions);
            });
    },

    isFilterSelectedInUi(taxonomy, slug) {
        if (
            Object.prototype.hasOwnProperty.call(this.pendingFilters, taxonomy)
        ) {
            return this.isSlugCoveredByPendingSelection(taxonomy, slug);
        }

        const stateValues = this.state.filters?.[taxonomy];

        if (
            Object.prototype.hasOwnProperty.call(
                this.state.filters || {},
                taxonomy
            )
        ) {
            if (Array.isArray(stateValues) && stateValues.length === 0) {
                return false;
            }

            if (stateValues) {
                const slugs = Array.isArray(stateValues)
                    ? stateValues
                    : [stateValues];
                const descendantsMap =
                    this.bootstrap.termDescendants?.[taxonomy] || {};

                return slugs.some(selectedSlug => {
                    if (selectedSlug === slug) {
                        return true;
                    }

                    if (!this.usesHierarchicalFilterSelection(taxonomy)) {
                        return false;
                    }

                    return (descendantsMap[selectedSlug] || []).includes(slug);
                });
            }

            return false;
        }

        return false;
    },

    getFilterNodeSelectionState(taxonomy, node) {
        if (!this.usesHierarchicalFilterSelection(taxonomy) || !node) {
            return null;
        }

        const slugs = this.getFilterNodeSlugs(node);

        if (slugs.length === 0) {
            return null;
        }

        let activeCount = 0;

        slugs.forEach(slug => {
            if (this.isSlugCoveredByPendingSelection(taxonomy, slug)) {
                activeCount += 1;
            }
        });

        if (activeCount === 0) {
            return 'unchecked';
        }

        if (activeCount === slugs.length) {
            return 'checked';
        }

        return 'indeterminate';
    },

    updatePendingFilterSelectionUi() {
        this.getFilterUiRoots().forEach(root => {
            root.querySelectorAll('[data-listing-filter-select]').forEach(
                button => {
                    const taxonomy = button.dataset.taxonomy || '';
                    const slug = button.dataset.termSlug || '';
                    const node = button.closest('[data-listing-filter-node]');
                    const hierarchicalState = this.getFilterNodeSelectionState(
                        taxonomy,
                        node
                    );
                    const selectionState =
                        hierarchicalState ||
                        (this.isFilterSelectedInUi(taxonomy, slug)
                            ? 'checked'
                            : 'unchecked');
                    const checkbox = button.querySelector(
                        '.listing__filter-checkbox'
                    );
                    const ariaPressed =
                        selectionState === 'checked'
                            ? 'true'
                            : selectionState === 'indeterminate'
                              ? 'mixed'
                              : 'false';

                    button.setAttribute('aria-pressed', ariaPressed);

                    if (checkbox) {
                        checkbox.classList.toggle(
                            'border-background-accent',
                            selectionState !== 'unchecked'
                        );
                        checkbox.classList.toggle(
                            'bg-background-accent',
                            selectionState !== 'unchecked'
                        );
                        checkbox.classList.toggle(
                            'border-grey-900',
                            selectionState === 'unchecked'
                        );
                        checkbox.classList.toggle(
                            'bg-white',
                            selectionState === 'unchecked'
                        );
                        checkbox.classList.toggle(
                            'listing__filter-checkbox--indeterminate',
                            selectionState === 'indeterminate'
                        );
                    }
                }
            );
        });

        this.refreshFilterCollapseUi();
    },

    expandPendingHierarchicalFilters() {
        this.filtersSidebar?.content
            ?.querySelectorAll('[data-listing-filter-select][data-term-slug]')
            .forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';
                const slug = button.dataset.termSlug || '';

                if (!this.usesHierarchicalFilterSelection(taxonomy)) {
                    return;
                }

                if (
                    !taxonomy ||
                    !slug ||
                    !(this.pendingFilters[taxonomy] || []).includes(slug)
                ) {
                    return;
                }

                const node = button.closest('[data-listing-filter-node]');

                if (!node) {
                    return;
                }

                const expanded = new Set(this.pendingFilters[taxonomy] || []);
                this.getFilterNodeSlugs(node).forEach(item =>
                    expanded.add(item)
                );
                this.pendingFilters[taxonomy] = this.sortFilterValuesByDomOrder(
                    taxonomy,
                    Array.from(expanded)
                );
            });
    },

    expandPendingFilterAncestors() {
        this.filtersSidebar?.content
            ?.querySelectorAll('[data-listing-filter-select][data-term-slug]')
            .forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';
                const slug = button.dataset.termSlug || '';

                if (
                    !taxonomy ||
                    !slug ||
                    !(this.pendingFilters[taxonomy] || []).includes(slug)
                ) {
                    return;
                }

                let parentNode = button
                    .closest('[data-listing-filter-node]')
                    ?.parentElement?.closest('[data-listing-filter-node]');

                while (parentNode) {
                    const toggle = parentNode.querySelector(
                        ':scope > .listing__filter-row [data-listing-filter-toggle]'
                    );

                    if (toggle) {
                        toggle.setAttribute('aria-expanded', 'true');
                    }

                    parentNode = parentNode.parentElement?.closest(
                        '[data-listing-filter-node]'
                    );
                }
            });
    },

    getFilterNodeSlugs(node) {
        return Array.from(
            node.querySelectorAll(
                '[data-listing-filter-select][data-term-slug]'
            )
        )
            .map(button => button.dataset.termSlug || '')
            .filter(Boolean);
    },

    sortFilterValuesByDomOrder(taxonomy, values) {
        const domOrder = Array.from(
            this.filtersSidebar?.content?.querySelectorAll(
                `[data-listing-filter-select][data-taxonomy="${taxonomy}"][data-term-slug]`
            ) || []
        )
            .map(button => button.dataset.termSlug || '')
            .filter(Boolean);

        return Array.from(new Set(values)).sort((left, right) => {
            const leftIndex = domOrder.indexOf(left);
            const rightIndex = domOrder.indexOf(right);

            if (leftIndex === -1 || rightIndex === -1) {
                return left.localeCompare(right, 'pl');
            }

            return leftIndex - rightIndex;
        });
    },

    handlePriceInput(event) {
        const input = event.target.closest(
            '[data-listing-price-min], [data-listing-price-max]'
        );

        if (!input) {
            return;
        }

        if (input.hasAttribute('data-listing-price-min')) {
            this.pendingPriceMin = input.value;
        } else {
            this.pendingPriceMax = input.value;
        }

        this.schedulePendingFiltersCountPreview();
    },

    cloneFilters(filters) {
        return Object.entries(filters || {}).reduce((acc, [key, value]) => {
            if (key === 'sort' || key === 'sort_by') {
                return acc;
            }

            acc[key] = Array.isArray(value) ? [...value] : value;
            return acc;
        }, {});
    },

    mergeFilters(...filterSets) {
        return filterSets.reduce((acc, filters) => {
            Object.entries(filters || {}).forEach(([taxonomy, value]) => {
                if (taxonomy === 'sort' || taxonomy === 'sort_by') {
                    return;
                }
                const values = Array.isArray(value) ? value : [value];
                const nextValues = [
                    ...(Array.isArray(acc[taxonomy]) ? acc[taxonomy] : []),
                    ...values.filter(Boolean),
                ];

                if (nextValues.length === 0) {
                    return;
                }

                acc[taxonomy] = this.sortFilterValuesByDomOrder(
                    taxonomy,
                    Array.from(new Set(nextValues))
                );
            });

            return acc;
        }, {});
    },

    collectSelectedFiltersFromMarkup() {
        const filters = {};

        this.filtersSidebar?.content
            ?.querySelectorAll(
                '[data-listing-filter-select][aria-pressed="true"]'
            )
            .forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';
                const slug = button.dataset.termSlug || '';

                if (!taxonomy || !slug) {
                    return;
                }

                if (!Array.isArray(filters[taxonomy])) {
                    filters[taxonomy] = [];
                }

                filters[taxonomy].push(slug);
            });

        return Object.entries(filters).reduce((acc, [taxonomy, values]) => {
            acc[taxonomy] = this.sortFilterValuesByDomOrder(
                taxonomy,
                Array.from(new Set(values))
            );
            return acc;
        }, {});
    },
};
